import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// ActionCreators / Selectors

import {
    changePage,
    changeData,
    clearFormSubmission,
    clearMethods,
    deleteForm,
    makeFormSnapshot,
    registerForm,
    saveSsap,
    setMethods,
    switchHyperReview,
    submitForm,
    validateForm,
    updateCachedSSN,
    updateCachedIMI,
} from '@actions/ssap/ssapActionCreators';

import {
    getCachedData,
    getServerDate,
    selectIfAdditionalResidencyEnabled,
    selectIfInlineEligibilityIgnoredForEscPage,
    selectIfMedicaidTerminationFeatureEnabled,
    selectIfShouldInvokeInlineEligibility,
    selectIfSogiFeatureEnabled,
    selectIsInlineCurrentIncomeEnabled,
    selectMedicaidEndDateOverride,
} from '@reducers/appReducer';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { executeCallbacks } from '@utils/miscellaneous/executeCallbacks';
import { getApplicantGuids, getHash, getPayloadForIMIUpdate, getPayloadForSsnUpdate, maskPII, mixValue, performCleanUpAndChecks } from '@utils/ssap/form-utils';
import { getCurrentAndMaxAchievedPages } from '@utils/miscellaneous/getCurrentAndMaxAchievedPages';
import { isEqual } from '@utils/miscellaneous/objectHelpers';
import { localStorageHyperDashboardTools } from '@utils/tools/localStorageHyperDashboardTools';
import { preventerTools } from '@utils/tools/preventerTools';
import { processJsonTools } from '@utils/tools/processJsonTools';
import { processUiProperties } from '@utils/tools/processUiProperties';
import { updateDirtyFlags } from '@utils/miscellaneous/updateDirtyFlags';
import { updateImiValues, updateSsnValues } from '@utils/householdMember/updateMaskedValues';

const Form = props => {
    // Props from Parent Component (usually it is Page)

    const { topLevelValidity, formName, pageName = '' } = props;

    // Props from Redux Store

    const cachedData = useSelector(getCachedData);
    const invokeInlineEligibility = useSelector(selectIfShouldInvokeInlineEligibility);
    const inlineCurrentIncomeEnabled = useSelector(selectIsInlineCurrentIncomeEnabled);
    const inlineEligibilityIgnoredForEscPage = useSelector(selectIfInlineEligibilityIgnoredForEscPage);
    const isSogiFeatureEnabled = useSelector(selectIfSogiFeatureEnabled);
    const medicaidTerminationFeatureEnabled = useSelector(selectIfMedicaidTerminationFeatureEnabled);
    const serverDate = useSelector(getServerDate);
    const medicaidEndDateOverride = useSelector(selectMedicaidEndDateOverride);
    const additionalResidencyEnabled = useSelector(selectIfAdditionalResidencyEnabled);

    const isFormValidationShown = localStorageHyperDashboardTools.getValueByPropName('isFormValidationShown', false);
    const isInvalidInputsShown = localStorageHyperDashboardTools.getValueByPropName('isInvalidInputsShown', false);

    const {
        adjustedFlow,
        data,
        data: { ssapApplicationId, maxAchievedPage },
        dataSnapshot,
        form,
        form: {
            [formName]: { isEligiblyForSave, isFormSubmitted, isNonStandaloneInputChanged, registeredInputs } = {
                registeredInputs: [],
                snapshot: '',
            },
        },
        environment: {
            isState,
            impersonation,
            user: { userId },
        },
        hyperReview,
        onLocalFormExit,
        onLocalFormSubmit,
        modals,
        page,
        relations,
        structure: { bloodRelationshipStructure, taxHouseholdStructure },
        formClasses,
        formProps = {},
    } = props;

    const [ localState, setState ] = useState({
        isGoingToExitWithoutSaving: false,
        frozenData: cloneObject(data),
    });

    const [ registration, updateRegistered ] = useState({
        registered: [],
        isRegistrationAccomplished: false,
    });

    const { registered, isRegistrationAccomplished } = registration;

    const adjustToSnapshot = values => {
        return values.map(({ inputId, initialInputValue }) => ({
            inputId,
            value: initialInputValue,
        }));
    };

    // Up to date primitive of registered inputs values to add as dependency for watching

    const registeredInputsToString = registeredInputs.map(input => input.valueOf()).toString();
    const registeredInputsToStringByNames = registeredInputs.map(input => input.inputName).toString();
    const registeredInputsValidityToStrings = registeredInputs.map(input => input.validOf()).toString();
    const registeredNonStandaloneInputs = registeredInputs.reduce((inputs, input) => {
        const { isInputStandalone } = input;

        return isInputStandalone ? inputs : [...inputs, input];
    }, []);

    const registeredNonStandaloneInputsToStrings = registeredNonStandaloneInputs
        .map(input => !input.isInputStandalone && input.inputValue)
        .toString();

    useEffect(() => {
        if(!form[formName]) {
            props.actions.registerForm(formName);
        }
        return () => {
            props.actions.deleteForm(formName);
        };
    }, [ formName ]);

    useEffect(() => {
        if(isRegistrationAccomplished) {
            props.actions.makeFormSnapshot(formName, JSON.stringify(registered));
        }
    }, [ isRegistrationAccomplished ]);

    useEffect(() => {
        if(registration.registered.length < registeredInputs.length && !isRegistrationAccomplished)  {
            updateRegistered(() => ({
                registered: adjustToSnapshot(registeredInputs),
                isRegistrationAccomplished: true,
            }));
        }
    }, [ registeredInputsToStringByNames ]);

    useEffect(() => {
        props.actions.setMethods(formName, {
            onFormSubmit,
            onFormExit,
            onFormSubmitForValidationOnly,
        });
        return () => {
            props.actions.clearMethods(formName);
        };
    }, [
        isEligiblyForSave,
        isNonStandaloneInputChanged,
        maxAchievedPage,
        registeredInputsToString,
        onLocalFormExit ? modals.length : '',
    ]);

    // Scrolling to the first error message on Form submit

    useEffect(() => {
        if(isFormSubmitted && !isEligiblyForSave) {
            const [element] = [...document.getElementsByClassName('error--msg')].filter(message =>
                message.className.match(/shown error--msg/),
            );

            if(element) {
                element.scrollIntoView({ behavior: 'smooth' });
            } else {
                console.log('Error message has not been found on the page');
            }
        }
    }, [ isFormSubmitted ]);

    // Watching for changes in inputs and auto-validation Form

    useEffect(() => {
        props.actions.validateForm(checkIfEligible(), formName);
    }, [registeredInputsValidityToStrings, registeredNonStandaloneInputsToStrings, topLevelValidity]);

    useEffect(() => {
        isFormSubmitted && isEligiblyForSave && props.actions.clearFormSubmission(formName);
    }, [isEligiblyForSave]);

    const getBody = (
        destinationPage,
        maxAchievedPageOverride,
        dataToUpdate,
        requestedByPageEffects = {},
        dataOverride = null,
    ) => {
        const currentFormData = dataOverride || data;

        const dataToSave = updateImiValues(
            updateSsnValues(currentFormData, mixValue(cachedData.ssns, dataToUpdate.ssnToUpdate)),
            cachedData.immigrationInfo,
        );

        const resolvedData = updateDirtyFlags(dataToSave, dataSnapshot, cachedData);

        const areDiffsInHhms = !isEqual(getApplicantGuids(resolvedData), getApplicantGuids(localState.frozenData));

        const { shouldRestrictNavigation, cleanedData } = performCleanUpAndChecks({
            dataAfterChanges: resolvedData,
            dataBeforeChanges: localState.frozenData,
            isSogiFeatureEnabled,
            additionalResidencyEnabled,
            medicaidTerminationFeatureEnabled,
            pageName,
            serverDate,
            medicaidEndDateOverride,
            bloodRelationshipCodes: relations?.codes,
            inlineCurrentIncomeEnabled,
            inlineEligibilityIgnoredForEscPage,
            isState: isState,
            loadedRelations: relations?.loadedRelations,
        });

        const inconsistencyCriteria = [areDiffsInHhms, isNonStandaloneInputChanged, shouldRestrictNavigation];

        const isDataPotentiallyInconsistent = inconsistencyCriteria.some(criteria => criteria);

        const { applicationStartDate, applicationGuid, addressConfidentialityParticipant } = cleanedData ?? {};
        const hasAddressConfidentialityParticipantChanged = addressConfidentialityParticipant !== localState.frozenData?.addressConfidentialityParticipant;

        const resolvedMaxAchievedPage = maxAchievedPageOverride ?? maxAchievedPage;
        
        const applicationData = JSON.stringify(
            processUiProperties({
                ...processJsonTools.convertTaxHousehold(
                    processJsonTools.convertBloodRelationships(maskPII(cleanedData), bloodRelationshipStructure),
                    taxHouseholdStructure,
                ),
                ...getCurrentAndMaxAchievedPages(destinationPage, page, resolvedMaxAchievedPage, isDataPotentiallyInconsistent, adjustedFlow),
            }),
        );

        const backendEffectsToRun = {
            csrOverride: impersonation,
            invokeInlineEligibility: inlineCurrentIncomeEnabled ? invokeInlineEligibility : false,
            invokeSAH: hasAddressConfidentialityParticipantChanged,
            ...dataToUpdate,
            ...requestedByPageEffects
        };

        return {
            application: applicationData,
            applicationId: ssapApplicationId,
            hashMap: getHash(applicationData, userId, applicationStartDate, applicationGuid),
            ...backendEffectsToRun
        };
    };

    const onFormSubmitForValidationOnly = (successCallbacks, failureCallbacks) => {
        props.actions.submitForm(formName);
        executeCallbacks(isEligiblyForSave ? successCallbacks : failureCallbacks);
    };

    const onFormSubmit = (destinationPage, callbacks, config = {}) => {
        const {
            exceptionAction = null,
            maxAchievedPageOverride = null,
            recalculateIncome = false,
            formDataOverride = null,
            skipFormEligibility = false,
        } = config;

        const currentFormData = formDataOverride || data;

        const { failCallback } = preventerTools.provideObject(
            callbacks && callbacks.find(cb => typeof cb === 'object'),
        );

        props.actions.submitForm(formName);

        if (isEligiblyForSave || skipFormEligibility) {
            if(onLocalFormSubmit) {
                onLocalFormSubmit(currentFormData);
                return null;
            }

            if (!exceptionAction) {
                const updateCallBacks = () => {
                    recalculateIncome && props.actions.clearFormSubmission(formName); // TODO: review if we need this
                    const mergeHyperReviewCallback = () =>
                        hyperReview ? [() => props.actions.switchHyperReview(false)] : [];

                    if (Array.isArray(callbacks)) {
                        return [...callbacks, ...mergeHyperReviewCallback()];
                    } else {
                        return [...mergeHyperReviewCallback()];
                    }
                };

                // TODO: Nice to move mask data payload to getBody, starts here
                const { ssnToUpdate } = getPayloadForSsnUpdate(currentFormData, localState, pageName);
                const { immigrationInfoToUpdate } = getPayloadForIMIUpdate(currentFormData, localState, pageName);

                if (ssnToUpdate) {
                    props.actions.updateCachedSSN(ssnToUpdate.applicantGuid, ssnToUpdate.ssn);
                }
                if (immigrationInfoToUpdate) {
                    const { applicantGuid, ...restProps } = immigrationInfoToUpdate;
                    props.actions.updateCachedIMI(applicantGuid, restProps);
                }
                // Ends here

                const areConsolesShown = localStorageHyperDashboardTools.getValueByPropName(
                    'areNonStandaloneConsolesShown',
                    false,
                );

                if (isNonStandaloneInputChanged) {
                    areConsolesShown &&
                        console.log(
                            `At least one non-standalone input has been changed: 
                        User is forced to continue application going page by page!`,
                        );
                } else {
                    areConsolesShown &&
                        console.log(
                            `Only standalone inputs might be changed: 
                        We still do not block jumping!`,
                        );
                }

                props.actions.saveSsap(
                    updateCallBacks(),
                    getBody(
                        destinationPage,
                        maxAchievedPageOverride,
                        { ssnToUpdate, immigrationInfoToUpdate },
                        {recalculateIncome},
                        currentFormData,
                    ),
                );
            } else {
                exceptionAction();
            }
        } else {
            executeCallbacks(failCallback);
        }
    };

    const onFormExit = (callbacks, bypassedData = undefined) => { // TODO: Review bypassed data here
        if (onLocalFormExit) {
            onLocalFormExit(localState.frozenData);
            return null;
        }

        props.actions.changeData(
            maskPII({
                ...(bypassedData ? bypassedData : localState.frozenData),
                maxAchievedPage,
            }),
        );

        setState(state => ({
            ...state,
            isGoingToExitWithoutSaving: true,
        }));

        executeCallbacks(callbacks);
    };

    const checkIfEligible = () => {
        const isApprovedByTopLevel = topLevelValidity === undefined ? true : topLevelValidity;

        if (registeredInputs.length === 0) {
            return {
                areAllInputsValid: true,
                isEligiblyForSave: isApprovedByTopLevel,
                isFormOnTopLevelValid: isApprovedByTopLevel,
                isNonStandaloneInputChanged: false,
                nonStandaloneInputsChanged: [],
            };
        }

        let result = true;

        registeredInputs.forEach(input => {
            if (
                input.inputIsValid === false ||
                (input.isInputRequired && (input.inputValue === '' || input.inputValue === null))
            ) {
                isInvalidInputsShown && console.log(input);
                result = false;
            }
        });

        const areConsolesShown = localStorageHyperDashboardTools.getValueByPropName(
            'areNonStandaloneConsolesShown',
            false,
        );

        areConsolesShown && console.log({ registeredNonStandaloneInputs });

        const nonStandaloneInputsChanged = registeredInputs.reduce((inputs, input) => {
            const { isInputStandalone, initialInputValue, inputValue } = input;

            return !isInputStandalone && inputValue !== initialInputValue ? [...inputs, input] : inputs;
        }, []);

        areConsolesShown && console.log({ nonStandaloneInputsChanged });

        return {
            isEligiblyForSave: isApprovedByTopLevel && result,
            isFormOnTopLevelValid: isApprovedByTopLevel,
            isNonStandaloneInputChanged: nonStandaloneInputsChanged.length > 0,
            areAllInputsValid: result,
            nonStandaloneInputsChanged: nonStandaloneInputsChanged,
        };
    };

    // Consoles start
    if (isFormValidationShown) {
        console.log(`>>>> Form: ${formName}`);
        console.log({
            isEligiblyForSave,
            topLevelValidity,
            registeredInputs: registeredInputs.map(input => ({
                inputIsValid: input.inputIsValid,
                isInputRequired: input.isInputRequired,
                inputName: input.inputName,
                onFormExit: input.onFormExit,
            })),
        });
    }
    // Consoles end

    const updateReactChildren = children => {
        return React.Children.map(children, child => {
            if (!React.isValidElement(child)) {
                return child;
            }
            let childProps = {};
            if (
                React.isValidElement(child) &&
                (typeof child.props.inputData === 'object' || child.props.underFormControl === true)
            ) {
                childProps = {
                    parentForm: {
                        name: formName,
                    },
                };
            }
            childProps.children = updateReactChildren(child.props.children);
            return React.cloneElement(child, childProps);
        });
    };

    return (
        <form 
            autoComplete="off" 
            className={`${formClasses && formClasses.join(' ')}`} 
            {...formProps}
            onSubmit={e => {e.preventDefault();}}
        >
            { form[formName] && updateReactChildren(props.children) }
        </form>
    );
};

const mapStateToProps = (state, ownProps) => ({

    environment: state.common.environment,
    modals: state.common.modals,

    adjustedFlow: state.ssap.adjustedFlow,
    data: ownProps.data ? ownProps.data : state.ssap.data,
    dataSnapshot: state.ssap.dataSnapshot,
    form: state.ssap.form,
    hyperReview: state.ssap.hyperReview,
    page: state.ssap.page,
    relations: state.ssap.relations,
    structure: state.ssap.structure
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            changeData,
            changePage,
            clearFormSubmission,
            clearMethods,
            deleteForm,
            makeFormSnapshot,
            registerForm,
            saveSsap,
            setMethods,
            submitForm,
            switchHyperReview,
            updateCachedIMI,
            updateCachedSSN,
            validateForm,
        },
        dispatch,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Form);
